/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:13:33 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { Many2ManyIntPKBaseSchema, Many2OneIntPKBaseSchema, schemaBuilder } from "lib/validation";
import {
  ProcessTemplateArtifact,
  ProcessTemplateArtifactValidationSchema,
  ProcessTemplateArtifactSchemaType,
  FormConfiguration,
  NumberingSequence,
} from "lib/schemas";
import { WritableModel } from "lib/schemas/abstract";

export enum FIELD_TYPES {
  TEXT = "1",
  TEXTAREA = "2",
  EMAIL = "3",
  CHECKBOX = "4",
  CHECKBOX_MULTIPLE = "5",
  SELECT = "6",
  SELECT_MULTIPLE = "7",
  RADIO_MULTIPLE = "8",
  FILE = "9",
  DATE = "10",
  DATE_TIME = "11",
  HIDDEN = "12",
  NUMBER = "13",
  URL = "14",
  DOB = "15",
  SECTION_BREAK = "16",
  GEO_COORDINATES = "17",
  ERP_RECORD = "18",
  MULTIPLE_FILE = "19",
  PAGE_TITLE = "20",
  BUTTON_CHECK = "21",
  MATRIX = "22",
  FORM_ENTRIES = "23",
  TITLE = "24",
  MULTI_FORM_ENTRIES = "25",
  AUTO_COMPLETE = "26",
  SOURCE_COMPLETE = "27",
  MATRIX_VIEW = "28",
  SIGNATURE_PAD = "31",
  IMAGE = "32",
  PHONE_NUMBER = "33",
  DYNAMIC_MATRIX = "34",
  AUTO_GENERATED_SEQUENCE = "35",
}

export type FIELD_TYPES_API_RESPONSE = Record<string, string>;

export enum WIDGET_CHOICES {
  WYSIWYG = "WYSIWYG Editor",
  MAP = "MAP",
  BROWSER_VIEW = "FILE VIEWER (Allows users to view uploaded files within the browser)",
}

export enum GeoLocationDataTypes {
  location_name = "Location Name",
  country = "Country",
  administrative_area = "Administrative Area",
  locality = "Locality / City",
  sublocality = "Sublocality",
  street = "Street",
}

export type TGeoFieldLocationDataTargetConfig = {
  fields_to_location_data_mapping: Record<string, (keyof typeof GeoLocationDataTypes)[]>;
};

export type TGeoFieldSettings = {
  show_map: boolean;
  default_to_current_location: boolean;
  default_latitude: number | string;
  default_longitude: number | string;
  geo_location_data_target_fields: TGeoFieldLocationDataTargetConfig;
  update_location_data_on_reverse_geocode: boolean;
};

export interface FormField extends ProcessTemplateArtifact, WritableModel<false> {
  label: string;
  readonly slug: string;
  field_type: FIELD_TYPES;
  required: boolean;
  visible: boolean;
  choices?: string;
  default?: string;
  placeholder_text?: string;
  help_text?: string;
  form: number;
  order: number;
  required_for_draft: boolean;
  copy_when_sending_notifications?: boolean;
  blocked_file_extensions?: string;
  allowed_file_extensions?: string;
  widget?: keyof typeof WIDGET_CHOICES;
  bind_user?: boolean;
  use_related?: boolean;
  entries_form?: number;
  entry_display_field?: number;
  linked_field?: number;
  unique_key?: boolean;
  composite_unique_key?: boolean;
  unique_error_message?: string;
  matrix_first_header_cell_label?: string;

  tabulate?: boolean;
  starts_new_row?: boolean;
  ends_row?: boolean;
  ends_table?: boolean;
  table_cell?: boolean;
  table_heading?: boolean;
  table_separate_label?: boolean;
  editable: boolean;
  ajax_source?: number;
  char_length?: number;
  max_file_size?: number;
  max_value?: string;
  min_value?: string;
  option_group?: number;
  data_set_reference?: number;
  display_label?: string;
  description?: string;
  validators?: number[];
  dynamic_matrix_settings?: IDynamicMatrixSettings;
  extra_configurations: { [key: string]: any };
  readonly resolved_choices?: { value: string; label: string }[];
  auto_generate_sequence?: NumberingSequence["id"];
  readonly form_title?: string;
}

// Using type alias to avoid index signature error when used a QueryParameters<T>.
// One can also use the spread syntax if you opt to make this an interface.
// https://bobbyhadz.com/blog/typescript-index-signature-for-type-is-missing-in-type
// https://github.com/microsoft/TypeScript/issues/15300
export type FormFieldAPIQueryParams = {
  form?: FormConfiguration["id"];
  field_type?: FormField["field_type"] | FormField["field_type"][];
  include_choices?: boolean;
};

export enum DynamicMatrixColumnTypes {
  text = "Text",
  number = "Number",
  date = "Date",
  select = "Select",
  file = "File",
}

export interface IDynamicMatrixColumnChoice {
  value: string;
  option: string;
}

export interface IDynamicMatrixColumn {
  name: string;
  type: keyof typeof DynamicMatrixColumnTypes;
  label: string;
  choices?: IDynamicMatrixColumnChoice[];
  default?: string;
  required?: boolean;
}

export interface IDynamicMatrixSettings {
  columns: IDynamicMatrixColumn[];
  minimum_rows: number;
  starting_rows: number;
  add_button_text: string;
}

type WriteOnlyFormField = Omit<FormField, "id" | "slug" | "resolved_choices" | "form_title">;

export const IDynamicMatrixColumnChoiceValidationSchema: schemaBuilder.SchemaOf<IDynamicMatrixColumnChoice> =
  schemaBuilder.object().shape({
    value: schemaBuilder.string().required("Value is required"),
    option: schemaBuilder.string().required("Option is required"),
  });

export const IDynamicMatrixColumnValidationSchema: schemaBuilder.SchemaOf<IDynamicMatrixColumn> = schemaBuilder
  .object()
  .shape({
    name: schemaBuilder.string().required("Column name is required with no spaces or special characters"),
    type: schemaBuilder.mixed().oneOf(Object.keys(DynamicMatrixColumnTypes)).required("Type is required"),
    label: schemaBuilder.string().required("Column label is required"),
    choices: schemaBuilder.array(IDynamicMatrixColumnChoiceValidationSchema),
    default: schemaBuilder.string().optional().nullable(),
    required: schemaBuilder.boolean().optional().nullable(),
  });

export const IDynamicMatrixSettingsValidationSchema: schemaBuilder.SchemaOf<IDynamicMatrixSettings> = schemaBuilder
  .object()
  .shape({
    columns: schemaBuilder.array(IDynamicMatrixColumnValidationSchema),
    minimum_rows: schemaBuilder.number().required("Minimum Rows is required"),
    starting_rows: schemaBuilder.number().required("Starting Rows is required"),
    add_button_text: schemaBuilder.string().optional().nullable(),
  });

export const IGEOSettingsValidationSchema = schemaBuilder.object().shape({
  show_map: schemaBuilder.boolean(),
  default_to_current_location: schemaBuilder.boolean(),
  default_latitude: schemaBuilder
    .mixed()
    .optional()
    .when("show_map", {
      is: true,
      then: schemaBuilder.number().required("Default latitude is required"),
    }),
  default_longitude: schemaBuilder
    .mixed()
    .optional()
    .when("show_map", {
      is: true,
      then: schemaBuilder.number().required("Default longitude is required"),
    }),
});

export const FormFieldValidationSchema: ProcessTemplateArtifactSchemaType<WriteOnlyFormField> =
  ProcessTemplateArtifactValidationSchema.shape({
    label: schemaBuilder.string().max(200).required("Label is required"),
    // field_type: schemaBuilder.mixed().oneOf(Object.values(FIELD_TYPES)).required("Status is required"),
    field_type: schemaBuilder.mixed().required("Status is required"), // Omitting oneOf to allow for custom field types. TODO: Load Field Types from API and use them here.
    required: schemaBuilder.boolean().required("Please indicate whether he field is required"),
    visible: schemaBuilder.boolean().required("Please indicate whether this field will be visible"),
    choices: schemaBuilder.string().optional().nullable(),
    default: schemaBuilder.string().nullable().optional(),
    placeholder_text: schemaBuilder.string().nullable().optional(),
    help_text: schemaBuilder.string().nullable().optional(),
    form: Many2OneIntPKBaseSchema().required("A form is required for creating a field"),
    order: schemaBuilder.number().required("Order is required"),
    required_for_draft: schemaBuilder.boolean().required("You must indictae whether this field is required for draft."),
    copy_when_sending_notifications: schemaBuilder.boolean().optional().nullable(),
    blocked_file_extensions: schemaBuilder.string().optional().nullable(),
    allowed_file_extensions: schemaBuilder.string().optional().nullable(),
    widget: schemaBuilder
      .mixed()
      .oneOf(["", null, ...Object.keys(WIDGET_CHOICES)])
      .optional()
      .nullable(),
    bind_user: schemaBuilder.boolean().optional().nullable(),
    use_related: schemaBuilder.boolean().optional().nullable(),
    entries_form: Many2OneIntPKBaseSchema().nullable().optional(),
    entry_display_field: Many2OneIntPKBaseSchema().nullable().optional(),
    linked_field: Many2OneIntPKBaseSchema().nullable().optional(),
    unique_key: schemaBuilder.boolean().optional().nullable(),
    composite_unique_key: schemaBuilder.boolean().optional().nullable(),
    unique_error_message: schemaBuilder.string().optional().nullable(),
    matrix_first_header_cell_label: schemaBuilder.string().optional().nullable(),

    tabulate: schemaBuilder.boolean().optional().nullable(),
    starts_new_row: schemaBuilder.boolean().optional().nullable(),
    ends_row: schemaBuilder.boolean().optional().nullable(),
    ends_table: schemaBuilder.boolean().optional().nullable(),
    table_cell: schemaBuilder.boolean().optional().nullable(),
    table_heading: schemaBuilder.boolean().optional().nullable(),
    table_separate_label: schemaBuilder.boolean().optional().nullable(),
    editable: schemaBuilder.boolean().required("Editable flag is required"),
    ajax_source: Many2OneIntPKBaseSchema().nullable().optional(),
    char_length: schemaBuilder.number().optional().nullable(),
    max_file_size: schemaBuilder.number().optional().nullable(),
    max_value: schemaBuilder.string().optional().nullable(),
    min_value: schemaBuilder.string().optional().nullable(),
    option_group: Many2OneIntPKBaseSchema().nullable().optional(),
    data_set_reference: Many2OneIntPKBaseSchema().nullable().optional(),
    display_label: schemaBuilder.string().optional().nullable(),
    description: schemaBuilder.string().optional().nullable(),
    validators: Many2ManyIntPKBaseSchema().optional().nullable(),
    dynamic_matrix_settings: IDynamicMatrixSettingsValidationSchema.optional().nullable().default(null),
    extra_configurations: schemaBuilder
      .mixed()
      .optional()
      .nullable()
      .when("field_type", {
        is: (field_type: string) => field_type === FIELD_TYPES.GEO_COORDINATES,
        then: IGEOSettingsValidationSchema.required("GeoCoordinate settings are required."),
      }),
    auto_generate_sequence: Many2OneIntPKBaseSchema().nullable().optional(),
  });
